import React, { useState, useEffect, useRef } from "react";
import { Button, Box, VStack, useTheme, useToast, Popover, PopoverTrigger, PopoverContent, PopoverBody, Portal, Divider, Text, Flex, Icon, useColorModeValue, Tooltip } from "@chakra-ui/react";
import { DownloadIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaFileExcel, FaChartBar, FaStore, FaRegListAlt, FaMapMarkerAlt } from "react-icons/fa";
import ExcelJS from "exceljs";

const Exports = ({ supplier, retailer, brands }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCheckersExports, setShowCheckersExports] = useState(false);
  const [showClicksExports, setShowClicksExports] = useState(false);
  const [showExtendedExport, setShowExtendedExport] = useState(false);
  const [articleBrands, setArticleBrands] = useState(brands);
  const theme = useTheme();
  const toast = useToast();
  const initialFocusRef = useRef();

  // Color mode values
  const buttonBg = useColorModeValue("primary1", "darkPrimary1");
  const buttonHoverBg = useColorModeValue("primary2", "darkPrimary2");
  const popoverBg = useColorModeValue("white", "gray.800");
  const popoverBorderColor = useColorModeValue("gray.200", "gray.700");
  const popoverShadow = useColorModeValue("lg", "dark-lg");
  const dividerColor = useColorModeValue("gray.200", "gray.700");
  const checkersBg = useColorModeValue("blue.50", "blue.900");
  const checkersHeaderBg = useColorModeValue("blue.100", "blue.800");

  const isVirtualRetailer = retailer === "MED" || retailer === "PET";
  const retailerFileName = retailer === "MED" ? "medirite" : retailer === "PET" ? "petshop" : retailer;

  useEffect(() => {
    if (retailer == "CHK" || retailer == "MED" || retailer == "PET") {
      setShowCheckersExports(true);
    }
    if (retailer == "CLI") {
      setShowClicksExports(true);
    }
  }, [retailer]);

  useEffect(() => {
    const userid = localStorage.getItem("userid");
    if (userid === "1" || userid === "2") {
      setShowExtendedExport(true);
    }
  }, []);

  const fetchCsv = async (vars, url, name) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(vars),
    };

    try {
      setIsLoading(true);
      const response = await fetch(url, requestOptions);
      setIsLoading(false);

      const contentType = response.headers.get("content-type");
      console.log("contentType: ", contentType);
      if (contentType && (contentType.includes("text/csv") || contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"))) {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${name}_${vars.supplier}_${retailerFileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast({
          title: "Success",
          description: `${name} file downloaded successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        console.log("error", response);
        toast({
          title: "Error",
          description: "An error occurred while downloading the file.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setIsLoading(false);
      toast({
        title: "Technical Error",
        description: "A technical error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchRaw = async (vars, url, name) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(vars),
    };

    // Helper functions
    const fontSize = 9;
    const fontFamily = "Calibri";

    function buildCsv(worksheet, rows) {
      rows.forEach((headerRow) => {
        const newRow = worksheet.addRow();
        let columnIndex = 1;

        headerRow.forEach((cell) => {
          const newCell = newRow.getCell(columnIndex);
          newCell.value = cell.value;

          if (cell.colspan && cell.colspan > 1) {
            worksheet.mergeCells(newRow.number, columnIndex, newRow.number, columnIndex + cell.colspan - 1);
            newCell.alignment = { horizontal: "center" };
            columnIndex += cell.colspan;
          } else {
            columnIndex += 1;
          }

          if (cell.hasOwnProperty("font")) {
            newCell.font = { size: cell.font.size, bold: cell.font.bold };
          }

          if (cell.hasOwnProperty("align")) {
            newCell.alignment = { horizontal: cell.align };
          }

          if (cell.hasOwnProperty("fill")) {
            newCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: cell.fill },
            };
          }

          if (cell.hasOwnProperty("border")) {
            newCell.border = {
              top: { style: "thin", color: { argb: cell.border } },
              left: { style: "thin", color: { argb: cell.border } },
              bottom: { style: "thin", color: { argb: cell.border } },
              right: { style: "thin", color: { argb: cell.border } },
            };
          }

          if (cell.rowspan && cell.rowspan > 1) {
            worksheet.mergeCells(newRow.number, columnIndex, newRow.number + cell.rowspan - 1, columnIndex);
          }
        });
      });
    }

    function width_csv2_storeSOH(sheet) {
      sheet.getColumn("A").width = 12;
      sheet.getColumn("B").width = 9;
      sheet.getColumn("C").width = 25;
      sheet.getColumn("D").width = 8;
      sheet.getColumn("E").width = 35;
      sheet.getColumn("F").width = 10;
      sheet.getColumn("G").width = 10;
      sheet.getColumn("H").width = 10;
      sheet.getColumn("I").width = 10;
      sheet.getColumn("J").width = 10;
      sheet.getColumn("K").width = 10;
      sheet.getColumn("L").width = 10;
      sheet.getColumn("M").width = 10;
      sheet.getColumn("N").width = 8;
      sheet.getColumn("O").width = 8;
      sheet.getColumn("P").width = 8;
      sheet.getColumn("Q").width = 12;
      sheet.getColumn("R").width = 12;
      sheet.getColumn("S").width = 12;

      const numberColumns = ["F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"];
      for (let i = 0; i < numberColumns.length; i++) {
        sheet.getColumn(numberColumns[i]).eachCell({ includeEmpty: true }, (cell) => {
          cell.numFmt = '_(* #,##0_);_(* (#,##0);_(* "-"??_);_(@_)';
        });
      }

      const alphabetArray = Array.from({ length: 26 }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i));
      for (let i = 0; i < alphabetArray.length; i++) {
        sheet.getColumn(alphabetArray[i]).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { size: fontSize, name: fontFamily };
        });
      }

      for (let row = 2; row <= 3; row++) {
        sheet.getRow(row).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { bold: true, size: fontSize, name: fontFamily };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF58B6C0" },
          };
        });
      }

      for (let row = 1; row <= 1; row++) {
        sheet.getRow(row).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { bold: true, size: fontSize, name: fontFamily };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDF0F9" } };
        });
      }
    }

    try {
      setIsLoading(true);
      const response = await fetch(url, requestOptions);
      const result = await response.json();
      console.log(result);
      if (result.status === 1 && result.data.length > 0) {
        const workbook = new ExcelJS.Workbook();

        // Loop through each sheet/region
        for (let i = 0; i < result.data.length; i++) {
          const sheetData = result.data[i];
          const worksheet = workbook.addWorksheet(sheetData.sheetName || `Region ${i + 1}`);
          buildCsv(worksheet, sheetData.head);
          buildCsv(worksheet, sheetData.body);
          width_csv2_storeSOH(worksheet);
        }

        // Generate blob and trigger download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${name}_${vars.supplier}_${retailerFileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: "Success",
          description: `${name} file downloaded successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "An error occurred while fetching the data.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Fetch error:", error);
      setIsLoading(false);
      toast({
        title: "Technical Error",
        description: "A technical error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchRawExtended = async (vars, url, name) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(vars),
    };

    const fontSize = 9;
    const fontFamily = "Calibri";

    function buildCsv(worksheet, rows) {
      rows.forEach((headerRow) => {
        const newRow = worksheet.addRow();
        let columnIndex = 1;

        headerRow.forEach((cell) => {
          const newCell = newRow.getCell(columnIndex);
          newCell.value = cell.value;

          if (cell.colspan && cell.colspan > 1) {
            worksheet.mergeCells(newRow.number, columnIndex, newRow.number, columnIndex + cell.colspan - 1);
            newCell.alignment = { horizontal: "center" };
            columnIndex += cell.colspan;
          } else {
            columnIndex += 1;
          }

          if (cell.hasOwnProperty("font")) {
            newCell.font = { size: cell.font.size, bold: cell.font.bold };
          }

          if (cell.hasOwnProperty("align")) {
            newCell.alignment = { horizontal: cell.align };
          }

          if (cell.hasOwnProperty("fill")) {
            newCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: cell.fill },
            };
          }

          if (cell.hasOwnProperty("border")) {
            newCell.border = {
              top: { style: "thin", color: { argb: cell.border } },
              left: { style: "thin", color: { argb: cell.border } },
              bottom: { style: "thin", color: { argb: cell.border } },
              right: { style: "thin", color: { argb: cell.border } },
            };
          }

          if (cell.rowspan && cell.rowspan > 1) {
            worksheet.mergeCells(newRow.number, columnIndex, newRow.number + cell.rowspan - 1, columnIndex);
          }
        });
      });
    }

    function colLetter(n) {
      let s = "";
      while (n > 0) {
        n--;
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26);
      }
      return s;
    }

    function applySheetFormatting(sheet, totalCols, numberStartCol, numberEndCol, headerRows) {
      // Set all columns to a default width
      for (let c = 1; c <= totalCols; c++) {
        const col = colLetter(c);
        sheet.getColumn(col).width = 10;
      }
      // Wider base columns
      sheet.getColumn("A").width = 12;
      if (totalCols > 30) {
        sheet.getColumn("B").width = 9;
        sheet.getColumn("C").width = 25;
      }

      // Number formatting for sales/SOH columns
      for (let c = numberStartCol; c <= numberEndCol; c++) {
        const col = colLetter(c);
        sheet.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
          cell.numFmt = '_(* #,##0_);_(* (#,##0);_(* "-"??_);_(@_)';
        });
      }

      // Font for all columns
      for (let c = 1; c <= totalCols; c++) {
        const col = colLetter(c);
        sheet.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { size: fontSize, name: fontFamily };
        });
      }

      // Header row formatting
      for (let row = headerRows[0]; row <= headerRows[1]; row++) {
        sheet.getRow(row).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { bold: true, size: fontSize, name: fontFamily };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF58B6C0" },
          };
        });
      }

      // Totals row
      sheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, size: fontSize, name: fontFamily };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDF0F9" } };
      });
    }

    function width_dashboard(sheet, cosmetics) {
      sheet.getColumn("A").width = 10;
      sheet.getColumn("B").width = 11;
      sheet.getColumn("C").width = 44;

      const allCols = [];
      for (let c = 4; c <= 42; c++) allCols.push(colLetter(c));
      allCols.forEach((col) => { sheet.getColumn(col).width = 9.5; });

      const numberColumns = ["D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","AA","AB","AD","AE","AG","AH"];
      numberColumns.forEach((col) => {
        sheet.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
          cell.numFmt = '_(* #,##0_);_(* (#,##0);_(* "-"??_);_(@_)';
        });
      });

      const alphabetArray = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
      alphabetArray.forEach((col) => {
        sheet.getColumn(col).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { size: fontSize, name: fontFamily };
        });
      });
      alphabetArray.forEach((col) => {
        const letter = "A" + col;
        sheet.getColumn(letter).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { size: fontSize, name: fontFamily };
        });
      });

      for (let row = 3; row <= 4; row++) {
        sheet.getRow(row).eachCell({ includeEmpty: true }, (cell) => {
          cell.font = { bold: true, size: fontSize, name: fontFamily };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58B6C0" } };
        });
      }

      if (cosmetics && cosmetics.brand_rows) {
        const excludedColumns = ["AC", "AF", "AI"];
        cosmetics.brand_rows.forEach((ind) => {
          sheet.getRow(ind).eachCell({ includeEmpty: true }, (cell) => {
            cell.font = { bold: true, size: fontSize, name: fontFamily };
            if (!excludedColumns.includes(cell.col)) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFBDF0F9" } };
            }
          });
        });
      }
    }

    function width_storeSOH_12m(sheet) {
      // 5 base + 12 value + 12 units + 4 SOH + 1 status + 3 dates = 37 cols
      sheet.getColumn("A").width = 12;
      sheet.getColumn("B").width = 9;
      sheet.getColumn("C").width = 25;
      sheet.getColumn("D").width = 10;
      sheet.getColumn("E").width = 35;
      applySheetFormatting(sheet, 37, 6, 33, [2, 3]);
    }

    function width_topStores_12m(sheet) {
      // 3 base + 12 value + 12 units + 4 SOH + 1 active = 32 cols
      sheet.getColumn("A").width = 12;
      sheet.getColumn("B").width = 9;
      sheet.getColumn("C").width = 25;
      applySheetFormatting(sheet, 32, 4, 31, [2, 3]);
    }

    function width_topSkus_12m(sheet) {
      // 2 base + 12 value + 12 units + 4 SOH + 1 stores = 31 cols
      sheet.getColumn("A").width = 10;
      sheet.getColumn("B").width = 35;
      applySheetFormatting(sheet, 31, 3, 30, [2, 3]);
    }

    try {
      setIsLoading(true);
      const response = await fetch(url, requestOptions);
      const result = await response.json();

      if (result.status === 1 && result.data.length > 0) {
        const workbook = new ExcelJS.Workbook();

        for (let i = 0; i < result.data.length; i++) {
          const sheetData = result.data[i];
          const worksheet = workbook.addWorksheet(sheetData.sheetName || `Region ${i + 1}`);
          buildCsv(worksheet, sheetData.head);
          buildCsv(worksheet, sheetData.body);

          switch (sheetData.sheetType) {
            case "dashboard":
              width_dashboard(worksheet, sheetData.cosmetics);
              break;
            case "storeSOH":
              width_storeSOH_12m(worksheet);
              break;
            case "topStores":
              width_topStores_12m(worksheet);
              break;
            case "topSkus":
              width_topSkus_12m(worksheet);
              break;
            default:
              break;
          }
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${name}_${vars.supplier}_${retailerFileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: "Success",
          description: `${name} file downloaded successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "An error occurred while fetching the data.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Fetch error:", error);
      setIsLoading(false);
      toast({
        title: "Technical Error",
        description: "A technical error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleExtendedSalesTrackerClick = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv_extended`;
      const name = "Extended Sales Tracker";
      const vars = { supplier, retailer, userid: localStorage.getItem("userid") };
      await fetchRawExtended(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  // Admin: 24 months of sales per article per month (units + value), the same numbers as the Sales page
  // (2026-10-07). Built server-side as a finished workbook — downloaded as-is.
  const handleSales24mClick = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/rep_sales/sales_24m_xlsx`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supplier, retailer, userid: localStorage.getItem("userid") }),
      });
      const ct = res.headers.get("content-type") || "";
      if (!res.ok || !ct.includes("spreadsheetml")) {
        let message = `HTTP ${res.status}`;
        try { message = (await res.json()).message || message; } catch (e) { /* not json */ }
        throw new Error(message);
      }
      const blob = await res.blob();
      const fileName = `${supplier} ${retailerFileName} - sales per article 24 months.xlsx`;
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      toast({ title: "Export failed", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalesTrackerClick = async () => {
    try {
      let endpoint = "sales_csv1";
      if (retailer === "MED") endpoint = "sales_csv_chk_medirite";
      else if (retailer === "PET") endpoint = "sales_csv_chk_petshop";
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/${endpoint}`;
      const name = "Sales Tracker";
      const vars = { supplier, retailer: retailer === "MED" || retailer === "PET" ? "CHK" : retailer, userid: localStorage.getItem("userid") };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const handleStoreSOHClick = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv2`;
      const name = "Store SOH Per Region";
      const vars = { supplier, retailer, userid: localStorage.getItem("userid") };
      await fetchRaw(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const handleTop10Click = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv3`;
      const name = "Top 10 Highest SOH Value";
      const vars = { supplier, retailer, userid: 1 };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const chk_salesTracker_wc = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv_chk_wc`;
      const name = "Sales Tracker WC";
      const vars = { supplier, retailer, userid: 1 };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const chk_salesTracker_kzn = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv_chk_kzn`;
      const name = "Sales Tracker KZN";
      const vars = { supplier, retailer, userid: 1 };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const chk_salesTracker_gt = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv_chk_gt`;
      const name = "Sales Tracker GT";
      const vars = { supplier, retailer, userid: 1 };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const chk_salesTracker_fs = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv_chk_fs`;
      const name = "Sales Tracker FS";
      const vars = { supplier, retailer, userid: 1 };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const chk_salesTracker_ec = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_csv_chk_ec`;
      const name = "Sales Tracker EC";
      const vars = { supplier, retailer, userid: 1 };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const cli_salesTracker_brand = async (brand) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/rep_sales/sales_tracker_cli_per_brand`;
      const name = `Sales Tracker ${brand}`;
      const vars = { supplier, retailer, userid: 1, brand };
      await fetchCsv(vars, url, name);
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const ExportButton = ({ icon, label, onClick, colorScheme = "teal", description = "" }) => (
    <Tooltip label={description} placement="right" hasArrow openDelay={500}>
      <Button
        leftIcon={<Icon as={icon} />}
        size="sm"
        variant="ghost"
        justifyContent="flex-start"
        width="100%"
        onClick={onClick}
        colorScheme={colorScheme}
        fontWeight="medium"
        py={2}
        borderRadius="md"
        transition="all 0.2s"
        _hover={{
          bg: `${colorScheme}.50`,
          _dark: {
            bg: `${colorScheme}.900`,
          },
          transform: "translateX(2px)",
        }}
      >
        {label}
      </Button>
    </Tooltip>
  );

  return (
    <Popover placement="bottom-start" autoFocus={false} initialFocusRef={initialFocusRef} closeOnBlur={true}>
      <PopoverTrigger>
        <Button ref={initialFocusRef} leftIcon={<DownloadIcon />} rightIcon={<ChevronDownIcon />} bg={buttonBg} color="white" _hover={{ bg: buttonHoverBg }} _active={{ bg: buttonHoverBg }} isLoading={isLoading} loadingText="Exporting..." boxShadow="sm" size="sm">
          Export
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="280px" bg={popoverBg} borderColor={popoverBorderColor} borderRadius="md" boxShadow={popoverShadow} _focus={{ outline: "none" }} zIndex={1400}>
          <PopoverBody p={0}>
            <Box p={3}>
              <Text fontWeight="medium" fontSize="sm" mb={2}>
                Standard Reports
              </Text>
              <VStack align="stretch" spacing={1}>
                <ExportButton icon={FaFileExcel} label="Sales Tracker" onClick={handleSalesTrackerClick} colorScheme="green" description="Export complete sales tracking data" />
                <ExportButton icon={FaStore} label="Store SOH per Region" onClick={handleStoreSOHClick} colorScheme="blue" description="Export stock on hand data by region" />
                <ExportButton icon={FaChartBar} label="Top 10 Highest SOH Value" onClick={handleTop10Click} colorScheme="purple" description="Export top 10 stores by stock value" />
              </VStack>
            </Box>

            {showExtendedExport && (
              <>
                <Divider borderColor={dividerColor} />
                <Box p={3}>
                  <Text fontWeight="medium" fontSize="sm" mb={2}>
                    Admin Reports
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    <ExportButton
                      icon={FaFileExcel}
                      label="Extended Sales Tracker"
                      onClick={handleExtendedSalesTrackerClick}
                      colorScheme="green"
                      description="Full sales tracker with 12 months on all sheets"
                    />
                    <ExportButton
                      icon={FaRegListAlt}
                      label="24-Month Sales per Article"
                      onClick={handleSales24mClick}
                      colorScheme="green"
                      description="Units and value per article per month, last 24 months — same figures as the Sales page"
                    />
                  </VStack>
                </Box>
              </>
            )}

            {showCheckersExports && (
              <>
                <Divider borderColor={dividerColor} />
                <Box p={3} bg={checkersBg}>
                  <Flex align="center" mb={2}>
                    <Box bg={checkersHeaderBg} px={2} py={1} borderRadius="md" width="100%" textAlign="center">
                      <Text fontWeight="bold" fontSize="sm">
                        {retailer === "MED" ? "MediRite Regional Reports" : retailer === "PET" ? "Petshop Regional Reports" : "Checkers Regional Reports"}
                      </Text>
                    </Box>
                  </Flex>
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="xs" fontWeight="medium" color="gray.600" _dark={{ color: "gray.300" }} ml={2}>
                      By Region:
                    </Text>
                    <ExportButton icon={FaMapMarkerAlt} label="Sales Tracker GT" onClick={chk_salesTracker_gt} colorScheme="cyan" />
                    <ExportButton icon={FaMapMarkerAlt} label="Sales Tracker KZN" onClick={chk_salesTracker_kzn} colorScheme="cyan" />
                    <ExportButton icon={FaMapMarkerAlt} label="Sales Tracker FS" onClick={chk_salesTracker_fs} colorScheme="cyan" />
                    <ExportButton icon={FaMapMarkerAlt} label="Sales Tracker WC" onClick={chk_salesTracker_wc} colorScheme="cyan" />
                    <ExportButton icon={FaMapMarkerAlt} label="Sales Tracker EC" onClick={chk_salesTracker_ec} colorScheme="cyan" />
                  </VStack>
                </Box>
              </>
            )}
            {showClicksExports && (
              <>
                <Divider borderColor={dividerColor} />
                <Box p={3} bg={checkersBg}>
                  <Flex align="center" mb={2}>
                    <Box bg={checkersHeaderBg} px={2} py={1} borderRadius="md" width="100%" textAlign="center">
                      <Text fontWeight="bold" fontSize="sm">
                        Clicks Sales Tracker per brand
                      </Text>
                    </Box>
                  </Flex>
                  <VStack align="stretch" spacing={1}>
                    {articleBrands.map((brand) => (
                      <React.Fragment key={brand}>
                        <ExportButton icon={FaRegListAlt} label={`Sales Tracker ${brand}`} onClick={() => cli_salesTracker_brand(brand)} colorScheme="orange" />
                        <Divider borderColor={dividerColor} opacity={0.5} my={1} />
                      </React.Fragment>
                    ))}
                  </VStack>
                </Box>
              </>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default Exports;
